/* eslint-disable react/prop-types */
import React, {
  lazy,
  Suspense,
  useEffect,
  useState,
  useRef,
  memo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectImages,
  selectAllImgIds,
  selectImageId,
} from "redux/image/selectors";
import {
  selectCurrentDropdownStatus,
  selectIsChangingDropdown,
} from "redux/app/selectors";
import { getIsUnsaveSegmentationSelector } from "redux/segmentation/selector";
import { selectOneSource, fetchInputData } from "redux/image/actions";
import { changeCurrentDropdownStatus } from "redux/app/actions";
import { fetchInitialData } from "redux/app/actions";
import ImageList from "./imageList";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import DropDown from "./DropDown";
import style from "./style.module.scss";

const FiltersTab = lazy(() => import("./FiltersTab"));

function InputDataPicker() {
  const dispatch = useDispatch();
  const selectedId = useSelector(selectImageId);
  const allImages = useSelector(selectImages);
  const allImgIds = useSelector(selectAllImgIds);
  const selectedStatus = useSelector(selectCurrentDropdownStatus);
  const isSelectChangeDropdown = useSelector(selectIsChangingDropdown);
  const isUnsaveSegmentation = useSelector(getIsUnsaveSegmentationSelector);
  const imageRefs = useRef({});

  const newPage = useSelector((state) => state.image.paginationByStatus.page);
  const newPageUp = useSelector(
    (state) => state.image.paginationByStatusScrollUp.page
  );
  const [tab, setTab] = useState("images");

  // Refs để lưu scroll position
  const scrollContainerRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const isLoadingTopRef = useRef(true);
  const previousItemCountRef = useRef(0);
  const canTriggerScrollEventsRef = useRef(false);
  const isScrollingIntoViewRef = useRef(false);
  const isChangeFilterRef = useRef(false);
  const [selectedImageLoaded, setSelectedImageLoaded] = useState(false);

  const handleChange = (_, newValue) => {
    setTab(newValue);
  };

  // Effect để maintain scroll position khi load data ở top
  useEffect(() => {
    if (isLoadingTopRef.current && scrollContainerRef.current) {
      const currentScrollHeight = scrollContainerRef.current.scrollHeight;
      const previousScrollHeight = previousScrollHeightRef.current;
      const heightDifference = currentScrollHeight - previousScrollHeight;

      if (
        heightDifference > 0 &&
        allImgIds.length > previousItemCountRef.current &&
        isLoadingTopRef.current
      ) {
        scrollContainerRef.current.scrollTop += heightDifference;
        isLoadingTopRef.current = false;
      }
      previousItemCountRef.current = allImgIds.length;
    }
    if (allImgIds.length > 0) {
      setTimeout(() => {
        canTriggerScrollEventsRef.current = true;
      }, 500);
    }
  }, [allImgIds.length]);

  useEffect(() => {
    if (!isSelectChangeDropdown) {
      isChangeFilterRef.current = false;
    }
  }, [isSelectChangeDropdown]);

  useEffect(() => {
    if (isSelectChangeDropdown) {
      dispatch(
        fetchInitialData({
          status: selectedStatus,
          page: 0,
          isSelectOneSourceSuccess: true,
        })
      );
      scrollContainerRef.current.scrollTop = 0;
      canTriggerScrollEventsRef.current = true;
      isLoadingTopRef.current = false;
    }
  }, [selectedStatus, isSelectChangeDropdown]);

  useEffect(() => {
    if (!selectedImageLoaded) return;
    const selectedEl = imageRefs.current[selectedId];
    if (!selectedEl) return;
    isScrollingIntoViewRef.current = true;

    selectedEl.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      isScrollingIntoViewRef.current = false;
    }, 600);
  }, [selectedImageLoaded, selectedId]);

  const handlePickImage = (event) => {
    const id = event.target.getAttribute("data-id");
    const index = event.target.getAttribute("index");
    if (isUnsaveSegmentation) {
      if (
        !window.confirm(
          "Your drawing is not saved. Do you want to leave the page?"
        )
      )
        return;
    }
    // dispatch(resetSegmentationMode());
    dispatch(selectOneSource({ id, index }));
  };

  function loadFunc({ isNotNextImage } = {}, isScrollDown) {
    if (!isScrollDown && scrollContainerRef.current) {
      previousScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
      isLoadingTopRef.current = true;
    }
    dispatch(
      fetchInputData({
        status: selectedStatus,
        page: isScrollDown ? newPage : newPageUp,
        isNotNextImage,
        isScrollDown,
      })
    );
  }

  const handleScrollDebounce = (isScrollDown) => {
    loadFunc({ isNotNextImage: true }, isScrollDown);
  };

  const handleScroll = (event) => {
    if (isChangeFilterRef.current) {
      return;
    }
    if (isScrollingIntoViewRef.current) {
      return;
    }
    if (!canTriggerScrollEventsRef.current) {
      return;
    }

    const toTop = Math.floor(event.target.scrollTop);
    if (toTop <= 5 && !isLoadingTopRef.current && newPageUp >= 0)
      handleScrollDebounce(false);
    if (
      [-1, 0, 1].includes(
        event.target.scrollHeight - toTop - event.target.clientHeight
      )
    )
      handleScrollDebounce(true);
  };

  const handleChangeStatus = (event) => {
    isChangeFilterRef.current = true;
    const value = event.target.value;
    // "All" (empty string) sends [], otherwise wrap in array for redux compatibility
    dispatch(changeCurrentDropdownStatus(value === "" ? [] : [value]));
  };

  return (
    <div className={style.container}>
      <div className={style.tabs}>
        <Tabs
          value={tab}
          onChange={handleChange}
          aria-label="filter tabs"
          variant="fullWidth"
          classes={{ indicator: style.indicator, root: style.tabWrapper }}
        >
          <Tab label="IMAGES" value="images" />
          <Tab label="FILTER" value="filter" />
        </Tabs>
        {tab === "images" && (
          <DropDown
            selectedStatus={
              Array.isArray(selectedStatus) && selectedStatus.length > 0
                ? selectedStatus[0]
                : ""
            }
            handleChangeStatus={handleChangeStatus}
          />
        )}
      </div>
      {tab === "images" ? (
        <ImageList
          allImgIds={allImgIds}
          allImages={allImages}
          selectedId={selectedId}
          handleScroll={handleScroll}
          handlePickImage={handlePickImage}
          imageRefs={imageRefs}
          scrollContainerRef={scrollContainerRef}
          setSelectedImageLoaded={setSelectedImageLoaded}
          selectedStatus={selectedStatus}
        />
      ) : (
        <Suspense fallback={<></>}>
          <FiltersTab setTab={setTab} />
        </Suspense>
      )}
    </div>
  );
}

export default memo(InputDataPicker);
